import { Fragment } from 'react';
import { motion } from 'motion/react';
import { Loader2 } from 'lucide-react';

import {
  computePatientQueueCounts,
  type PatientNavigatorItemData,
  type PatientQueueCounts,
} from '@/features/patient-navigator';
import { cn } from '@/lib/utils';

interface QueueSummaryProps {
  patients: PatientNavigatorItemData[];
  isLoading: boolean;
}

interface SummarySegment {
  key: string;
  text: string;
  className: string;
}

function buildSegments(counts: PatientQueueCounts): SummarySegment[] {
  const totalNoun = counts.total === 1 ? 'patient' : 'patients';
  const segments: SummarySegment[] = [
    {
      key: 'total',
      text: `${counts.total} ${totalNoun} waiting on you`,
      className: 'text-white/80',
    },
  ];

  if (counts.urgent > 0) {
    segments.push({
      key: 'urgent',
      text: `${counts.urgent} urgent`,
      className: 'text-red-300',
    });
  }
  if (counts.routine > 0) {
    segments.push({
      key: 'routine',
      text: `${counts.routine} routine`,
      className: 'text-orange-300',
    });
  }
  if (counts.inProgress > 0) {
    segments.push({
      key: 'in-progress',
      text: `${counts.inProgress} in progress`,
      className: 'text-emerald-300',
    });
  }

  return segments;
}

const SUMMARY_MOTION = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.2, delay: 0.04 },
} as const;

/**
 * QueueSummary — single-line, dynamic queue summary rendered as the
 * dashboard header's subtitle.
 *
 * Replaces the previous stat-card grid. Filtering happens via the queue's
 * tabs; this component is presentation-only and intentionally non-interactive.
 */
export function QueueSummary({ patients, isLoading }: QueueSummaryProps) {
  const counts = computePatientQueueCounts(patients);

  // Loading takes priority over the empty state: showing "you're caught up"
  // before the queue has loaded is dangerously misleading — a clinician could
  // leave thinking no patients are waiting when the list simply hasn't arrived.
  if (isLoading) {
    return (
      <motion.p
        role="status"
        aria-busy="true"
        aria-label="Loading patient queue"
        className="flex items-center gap-2 text-sm font-medium text-white/70"
        {...SUMMARY_MOTION}
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        Loading patient queue…
      </motion.p>
    );
  }

  if (counts.total === 0) {
    return (
      <motion.p
        role="status"
        aria-label="Queue summary"
        className="text-sm font-medium text-white/55"
        {...SUMMARY_MOTION}
      >
        No patients waiting — you’re caught up.
      </motion.p>
    );
  }

  const segments = buildSegments(counts);

  return (
    <motion.p
      role="status"
      aria-label="Queue summary"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium tabular-nums"
      {...SUMMARY_MOTION}
    >
      {segments.map((segment, index) => (
        <Fragment key={segment.key}>
          {index > 0 ? (
            <span className="text-white/25" aria-hidden="true">
              ·
            </span>
          ) : null}
          <span className={cn(segment.className)}>{segment.text}</span>
        </Fragment>
      ))}
    </motion.p>
  );
}
